import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AnalysisEnseigne, CreateShopInput, ShopType } from '@reims-local/types';
import { adminApi } from '../lib/api';

const SHOP_TYPE_OPTIONS: Array<{ value: ShopType; label: string }> = [
  { value: 'SHOP_STANDARD', label: 'Standard (mode, déco, high-tech)' },
  { value: 'SHOP_FOOD', label: 'Alimentation (traiteur, épicerie)' },
  { value: 'RESTAURANT', label: 'Restaurant (réservation table)' },
  { value: 'SERVICE', label: 'Service (coiffure, beauté)' },
];

export function AddShop() {
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState<AnalysisEnseigne[]>([]);
  const [search, setSearch] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [form, setForm] = useState<CreateShopInput>({
    name: '',
    email: '',
    address: '',
    phone: '',
    shop_type: 'SHOP_STANDARD',
    partner_included: false,
    description: '',
    admin_note: '',
    from_analysis: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminApi
      .getAnalysis()
      .then((res) => setAnalysis(res.data))
      .catch(() => setAnalysis([]));
  }, []);

  const results = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return analysis
      .filter((e) => e.nom.toLowerCase().includes(q) || (e.categorie ?? '').toLowerCase().includes(q))
      .slice(0, 10);
  }, [analysis, search]);

  function selectFromAnalysis(e: AnalysisEnseigne) {
    setForm({
      name: e.nom,
      email: e.email ?? '',
      address: e.adresse ?? '',
      phone: e.telephone ?? '',
      shop_type: e.type,
      partner_included: e.partenaire === 'vitrines_reims',
      description: '',
      admin_note: e.notes ?? '',
      from_analysis: true,
    });
    setSearch(e.nom);
    setShowResults(false);
  }

  async function submit(ev: React.FormEvent) {
    ev.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.createShop(form);
      navigate(`/shops/${res.data.id}`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <h2>Ajouter une boutique</h2>

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="form-row">
          <label>Rechercher dans le fichier d'analyse Vitrines de Reims</label>
          <input
            placeholder="Tapez un nom d'enseigne…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setShowResults(true);
            }}
            onFocus={() => setShowResults(true)}
          />
          {showResults && results.length > 0 && (
            <div className="search-results">
              {results.map((e) => (
                <div key={e.nom} className="item" onClick={() => selectFromAnalysis(e)}>
                  <strong>{e.nom}</strong>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {e.adresse} · {e.type} · {e.potentiel}
                  </div>
                </div>
              ))}
              <div
                className="item manual"
                onClick={() => {
                  setShowResults(false);
                  setForm((f) => ({ ...f, from_analysis: false }));
                }}
              >
                — Saisir manuellement une enseigne non référencée —
              </div>
            </div>
          )}
          {analysis.length === 0 && (
            <div className="muted" style={{ marginTop: 4 }}>
              Fichier d'analyse non chargé. Utilisez Paramètres &gt; Fichier d'analyse.
            </div>
          )}
        </div>
      </div>

      <form className="card" onSubmit={submit}>
        {error && <div className="alert alert-error">{error}</div>}

        <div className="form-row">
          <label>Nom *</label>
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>

        <div className="form-row">
          <label>Email du commerçant *</label>
          <input
            required
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </div>

        <div className="form-row">
          <label>Adresse</label>
          <input value={form.address ?? ''} onChange={(e) => setForm({ ...form, address: e.target.value })} />
        </div>

        <div className="form-row">
          <label>Téléphone</label>
          <input value={form.phone ?? ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </div>

        <div className="form-row">
          <label>Type de boutique *</label>
          <select
            value={form.shop_type}
            onChange={(e) => setForm({ ...form, shop_type: e.target.value as ShopType })}
          >
            {SHOP_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-row">
          <label>
            <input
              type="checkbox"
              checked={form.partner_included ?? false}
              onChange={(e) => setForm({ ...form, partner_included: e.target.checked })}
              style={{ width: 'auto', marginRight: 8 }}
            />
            Inclus au contrat Vitrines de Reims (pas de facturation directe)
          </label>
        </div>

        <div className="form-row">
          <label>Description (optionnel)</label>
          <textarea
            rows={3}
            value={form.description ?? ''}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>

        <div className="form-row">
          <label>Notes internes admin (optionnel)</label>
          <textarea
            rows={2}
            value={form.admin_note ?? ''}
            onChange={(e) => setForm({ ...form, admin_note: e.target.value })}
          />
        </div>

        <button className="primary" type="submit" disabled={loading}>
          {loading ? 'Création…' : 'Créer la boutique'}
        </button>
      </form>
    </>
  );
}
